import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { add, format, formatHuman, parse, zero } from '@fint/money';
import { EXPENSE_DIMENSIONS, INCOME_DIMENSIONS, type BreakdownResponse, type BreakdownRow, type ReportChange, type ReportDimension } from '@fint/validation';
import { api, qs } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Period } from '@/lib/dates';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency, useGstEnabled } from '@/hooks/useOrgSettings';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, Money, RankedBars, SkeletonTable, type RankedBarRow } from '@/features/common/ui';
import type { Basis } from '@/features/dashboard/shared';
import { describeChange, drillFor, windowLabel } from './drill';

/**
 * "Where the money came from" / "Where it went", split the way the reader
 * asks: income by item, HSN/SAC, customer or category; spend by category,
 * vendor or project.
 *
 * ── WHY A DIMENSION SWITCH AND NOT FOUR CARDS ──
 * The question is one question - where did it come from - with four
 * answers a person wants one at a time. Four cards would be four lists of
 * the same money on one screen, and the eye would have to reconcile them.
 * One card remembers which answer this person wanted last (per browser,
 * nothing more), and defaults to the one most books need: the item.
 *
 * ── WHAT A ROW SAYS ──
 * Hover, focus or tap a row and it says the seven things every chart in
 * Fint says (the contract is in the reporting audit): what it is, how much
 * and on which basis, its share, how many records and the biggest of them,
 * how it compares with the earlier windows, and where a click goes. The
 * click opens the list filtered to exactly those records.
 */
const LABELS: Record<ReportDimension, string> = {
  item: 'Item',
  hsn: 'HSN/SAC',
  customer: 'Customer',
  category: 'Category',
  vendor: 'Vendor',
  project: 'Project',
};

const TOP = 7;

function remembered(side: 'income' | 'expense'): ReportDimension | null {
  try {
    const raw = localStorage.getItem(`fint:breakdown:${side}`);
    const allowed: readonly string[] = side === 'income' ? INCOME_DIMENSIONS : EXPENSE_DIMENSIONS;
    return raw && allowed.includes(raw) ? (raw as ReportDimension) : null;
  } catch {
    return null;
  }
}

export function BreakdownCard({
  side,
  period,
  basis,
  title,
  className,
}: {
  side: 'income' | 'expense';
  period: Period;
  basis: Basis;
  title: string;
  className?: string;
}) {
  const org = useActiveOrg();
  const currency = useBaseCurrency();
  const gstEnabled = useGstEnabled();
  const [chosen, setChosen] = useState<ReportDimension | null>(() => remembered(side));
  const [by, setBy] = useState<ReportDimension>(() => remembered(side) ?? (side === 'income' ? 'item' : 'category'));
  const [expanded, setExpanded] = useState(false);
  const choose = (dimension: ReportDimension) => {
    setChosen(dimension);
    setBy(dimension);
    setExpanded(false);
    try {
      localStorage.setItem(`fint:breakdown:${side}`, dimension);
    } catch {
      // A private window; the choice simply is not remembered.
    }
  };

  const query = useQuery({
    queryKey: ['report-breakdown', org?.id, side, by, period.from, period.to, basis],
    enabled: !!org?.id,
    queryFn: () => api.get<BreakdownResponse>(`/reports/breakdown${qs({ side, by, from: period.from, to: period.to, basis })}`),
  });

  const dimensions = side === 'income' ? INCOME_DIMENSIONS : EXPENSE_DIMENSIONS;
  const money = (amount: string) => formatHuman(parse(amount, currency));
  const data = query.data;
  /**
   * A cash book - salary and deposits, no invoices - has nothing to split
   * by item: the item view would be one bar, "Received, not on an invoice".
   * Until the person picks a split themselves, such a book opens on the
   * category split, which is the answer that means something there.
   */
  useEffect(() => {
    if (chosen || !data || side !== 'income' || by !== 'item') return;
    if (data.rows.length > 0 && data.rows.every((row) => row.kind === 'deposits')) setBy('category');
  }, [chosen, data, side, by]);

  const rows = useMemo<RankedBarRow[]>(() => {
    if (!data) return [];
    const counted = data.rows.filter((row) => !row.excluded);
    const shown = expanded ? counted : counted.slice(0, TOP);
    const rest = expanded ? [] : counted.slice(TOP);
    const window = { from: data.from, to: data.to };
    const out: RankedBarRow[] = shown.map((row) => {
      const drill = drillFor(side, by, row, window, basis);
      return {
        id: row.key,
        name: row.name,
        colour: row.colour,
        emoji: row.emoji,
        amount: row.amount,
        share: row.share,
        meta: metaOf(row, by),
        href: drill?.href,
        detail: <RowDetail row={row} side={side} by={by} basis={basis} currency={currency} gstEnabled={gstEnabled} total={data.total} drillLabel={drill?.label ?? null} />,
      };
    });
    if (rest.length > 0) {
      const restTotal = rest.reduce((sum, row) => add(sum, parse(row.amount, currency)), zero(currency));
      out.push({
        id: '__other__',
        name: `Other (${rest.length})`,
        colour: 'slate',
        emoji: null,
        amount: format(restTotal),
        share: rest.reduce((sum, row) => sum + row.share, 0),
      });
    }
    for (const row of data.rows.filter((r) => r.excluded)) {
      out.push({ id: row.key, name: row.name, colour: row.colour, emoji: row.emoji, amount: row.amount, share: 0, excluded: true, meta: 'marked out of the totals' });
    }
    return out;
  }, [data, expanded, side, by, basis, currency, gstEnabled]);

  const counted = data?.rows.filter((row) => !row.excluded).length ?? 0;
  const previous = data?.previous ? describeChange(data.previous, money) : null;

  return (
    <Card className={className} data-testid={`breakdown-${side}`}>
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{title}</CardTitle>
          <div role="group" aria-label={`Split ${side === 'income' ? 'income' : 'spending'} by`} className="flex flex-wrap gap-1" data-testid={`breakdown-${side}-by`}>
            {dimensions.map((dimension) => (
              <button
                key={dimension}
                type="button"
                aria-pressed={by === dimension}
                onClick={() => choose(dimension)}
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors touch:min-h-11 touch:px-3',
                  by === dimension ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted active:bg-muted',
                )}
              >
                {LABELS[dimension]}
              </button>
            ))}
          </div>
        </div>
        {data ? (
          <p className="text-xs text-muted-foreground" data-testid={`breakdown-${side}-summary`}>
            <Money value={data.total} currency={currency} /> across {counted} {counted === 1 ? LABELS[by].toLowerCase() : pluralOf(by)}
            {previous && data.previous && data.previous.percent !== null ? (
              <>
                {' · '}
                <Tone tone={previous.tone} side={side}>{previous.text}</Tone> vs {windowLabel(data.previous)}
              </>
            ) : null}
          </p>
        ) : null}
      </CardHeader>
      <CardContent>
        {query.isError ? <ErrorState error={query.error} onRetry={() => { void query.refetch(); }} /> : null}
        {query.isPending ? <SkeletonTable rows={4} /> : null}
        {data ? (
          <>
            <RankedBars
              rows={rows}
              renderValue={(amount) => <Money value={amount} currency={currency} showSymbol={false} />}
              emptyLabel={side === 'income' ? `No money in during ${period.label}.` : `No spending during ${period.label}.`}
            />
            {!expanded && counted > TOP ? (
              <button type="button" onClick={() => setExpanded(true)} className="mt-3 text-xs text-primary hover:underline">
                Show the other {counted - TOP}
              </button>
            ) : null}
            {expanded && counted > TOP ? (
              <button type="button" onClick={() => setExpanded(false)} className="mt-3 text-xs text-primary hover:underline">
                Show fewer
              </button>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function pluralOf(by: ReportDimension): string {
  switch (by) {
    case 'item':
      return 'items';
    case 'hsn':
      return 'HSN/SAC codes';
    case 'customer':
      return 'customers';
    case 'category':
      return 'categories';
    case 'vendor':
      return 'vendors';
    case 'project':
      return 'projects';
  }
}

/** The small print beside a name: the code an item sells under, or what kind of row it is. */
function metaOf(row: BreakdownRow, by: ReportDimension): string | null {
  if (by === 'item') {
    if (row.kind === 'line-name') return row.hsnSac ? `typed · ${row.hsnSac}` : 'typed on the invoice';
    if (row.kind === 'item') return row.hsnSac ? row.hsnSac : null;
  }
  return null;
}

function Tone({ tone, side, children }: { tone: 'up' | 'down' | 'flat'; side: 'income' | 'expense'; children: React.ReactNode }) {
  // A rise in income is good news; a rise in spend is not. Flat is neither.
  const good = tone === 'flat' ? null : (tone === 'up') === (side === 'income');
  return <span className={cn('font-medium tabular-nums', good === true && 'text-success', good === false && 'text-destructive')}>{children}</span>;
}

/** The hover card: the seven things, in order. */
function RowDetail({
  row,
  side,
  by,
  basis,
  currency,
  gstEnabled,
  total,
  drillLabel,
}: {
  row: BreakdownRow;
  side: 'income' | 'expense';
  by: ReportDimension;
  basis: Basis;
  currency: string;
  gstEnabled: boolean;
  total: string;
  drillLabel: string | null;
}) {
  const money = (amount: string) => formatHuman(parse(amount, currency));
  const what = kindLabel(row, by);
  const measure = side === 'income' ? (basis === 'accrual' ? 'Invoiced' : 'Received') : 'Spent';
  const suffix = gstEnabled ? (side === 'income' ? ', ex-GST' : ', net of GST') : '';
  const records = side === 'income' ? (row.kind === 'deposits' ? 'deposit' : 'invoice') : 'expense';
  const quantity = row.quantity !== null ? Number(row.quantity) : null;
  const catalogueGap = row.catalogueRate && row.averageRate ? Number(row.averageRate) / Number(row.catalogueRate) - 1 : null;
  const line = (label: string, value: React.ReactNode, key: string) => (
    <div key={key} className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </div>
  );
  const change = (key: string, c: ReportChange | null) => {
    if (!c) return null;
    if (c.percent === null && Number(c.amount) === 0) {
      return line(`vs ${windowLabel(c)}`, <span className="text-muted-foreground">nothing recorded then</span>, key);
    }
    const described = describeChange(c, money);
    return line(`vs ${windowLabel(c)}`, <Tone tone={described.tone} side={side}>{described.text}</Tone>, key);
  };
  const sameWindow = row.previous && row.year && row.previous.from === row.year.from && row.previous.to === row.year.to;
  return (
    <div className="space-y-1.5" data-testid="breakdown-detail">
      <div>
        <p className="font-medium">{row.name}</p>
        {what ? <p className="text-muted-foreground">{what}</p> : null}
      </div>
      <dl className="space-y-0.5">
        {line(`${measure}${suffix}`, <span className="font-medium">{money(row.amount)}</span>, 'amount')}
        {line(`Share of ${side === 'income' ? 'income' : 'spend'}`, `${(row.share * 100).toFixed(0)}% of ${money(total)}`, 'share')}
        {line('Behind it', `${row.count} ${row.count === 1 ? records : `${records}s`}`, 'count')}
        {quantity !== null && quantity > 0
          ? line('Quantity', `${row.quantity}${row.averageRate ? ` · avg ${money(row.averageRate)} each` : ''}`, 'quantity')
          : null}
        {row.catalogueRate
          ? line(
              'Catalogue rate',
              catalogueGap === null || Math.abs(catalogueGap) < 0.005
                ? money(row.catalogueRate)
                : `${money(row.catalogueRate)} · billed ${Math.round(Math.abs(catalogueGap) * 100)}% ${catalogueGap < 0 ? 'under' : 'over'}`,
              'catalogue',
            )
          : null}
        {row.top && row.top.share < 0.999 ? line('Biggest', `${row.top.name} · ${money(row.top.amount)} (${Math.round(row.top.share * 100)}%)`, 'top') : null}
        {row.top && row.top.share >= 0.999 && row.count > 1 ? line(side === 'income' ? 'All from' : 'All under', row.top.name, 'top') : null}
        {change('previous', row.previous)}
        {sameWindow ? null : change('year', row.year)}
      </dl>
      {drillLabel ? <p className="text-primary">{drillLabel}</p> : null}
    </div>
  );
}

function kindLabel(row: BreakdownRow, by: ReportDimension): string | null {
  switch (row.kind) {
    case 'item':
      return row.hsnSac ? `Catalogue item · ${row.hsnSac}` : 'Catalogue item';
    case 'line-name':
      return row.hsnSac ? `Typed on the invoice, not a catalogue item · ${row.hsnSac}` : 'Typed on the invoice, not a catalogue item';
    case 'hsn':
      return by === 'hsn' ? 'HSN/SAC code' : null;
    case 'customer':
      return 'Customer';
    case 'vendor':
      return 'Vendor';
    case 'category':
      return 'Category';
    case 'project':
      return 'Project';
    case 'deposits':
      return 'Money received with no invoice behind it';
    case 'none':
      return null;
  }
}
